// Convert a MediaRecorder capture to 16kHz mono FLAC (compression level 5),
// base64-encoded for the /ai/transcribe route (dictation format picker, 0020).
//
// libflacjs is libFLAC compiled to asm.js/WASM — dynamically imported so it
// lands in its own chunk and is only paid for when FLAC is selected. The
// variant imported here is the plain (non-minified) asm.js build
// `libflacjs/dist/libflac.js`, which initializes synchronously in the
// browser (no separate .mem/.wasm fetch, no FLAC_SCRIPT_LOCATION wiring) —
// the simplest reliable variant for Vite.
//
// The lib's Encoder utility class (libflacjs/lib/encoder) is CJS and pulls
// in its own `require("./utils/data-utils")` at module scope, which Vite's
// dep-optimizer cannot always rewrite in dynamic-import edge cases — so
// this module drives the raw libFLAC C API directly (create →
// init_encoder_stream → process_interleaved → finish → delete) and does the
// two small jobs the utility class would have done itself: merging the
// encoded chunks and patching STREAMINFO (total_samples / frame sizes /
// MD5) into the header. A raw merge alone would leave placeholder metadata
// (total_samples=0), which some strict decoders dislike.

import { decodeToMonoPcm16 } from "./audioToWav";

// libflacjs ships no usable top-level typings for the browser entry
// (index.d.ts is a Node factory with export =), so type the module we
// actually import directly.
type FlacLib = typeof import("libflacjs/dist/libflac.js");

// STREAMINFO field offsets as ABSOLUTE byte positions in the FLAC file
// (libflacjs's addFLACMetaData uses the same numbers RELATIVE to its
// offset=4 base — don't confuse the two). Layout: "fLaC" magic (0-3),
// block header (4-7), then STREAMINFO content from byte 8: min/max
// blocksize (8-11), min/max framesize (12-17), sample_rate/channels/bps
// (18-21), total_samples (22-25, top nibble in byte 21 stays 0 below
// 2^32 samples), MD5 (26-41).
const SI_MIN_FRAMESIZE = 12;
const SI_MAX_FRAMESIZE = 15;
const SI_TOTAL_SAMPLES = 22;
const SI_MD5 = 26;

// The encoder's write callback pushes each encoded chunk here.
type WriteCb = (data: Uint8Array, bytes: number, samples: number, currentFrame: number) => void;

export async function audioBlobToFlacBase64(blob: Blob): Promise<{ base64: string; format: string }> {
  const { samples, sampleRate } = await decodeToMonoPcm16(blob);

  // Dynamic import — code-split; the asm.js lib is heavy (~1MB).
  const flac = (await import("libflacjs/dist/libflac.js")) as FlacLib;

  // Int16 PCM → Int32 (libFLAC consumes 32-bit samples; 16-bit values
  // shifted left by 16 preserve the full 16-bit precision).
  const pcm32 = new Int32Array(samples.length);
  for (let i = 0; i < samples.length; i++) pcm32[i] = samples[i] << 16;

  const chunks: Uint8Array[] = [];
  let metadata: Record<string, unknown> | null = null;

  // [1] CREATE — 16kHz, mono, 16bps, compression 5, total_samples known
  // (all-at-once encoding, so STREAMINFO can be exact).
  const enc = flac.create_libflac_encoder(sampleRate, 1, 16, 5, samples.length, false, 0);
  if (!enc) throw new Error("FLAC encoding unavailable");

  try {
    // [2] INIT — collect encoded output + metadata summary.
    const writeCb: WriteCb = (data) => {
      chunks.push(new Uint8Array(data));
    };
    const metaCb = (m: Record<string, unknown>) => {
      if (m) metadata = m;
    };
    const status = flac.init_encoder_stream(enc, writeCb, metaCb as never);
    if (status !== 0) throw new Error("FLAC encoding unavailable");

    // [3] ENCODE — one interleaved pass over the mono samples.
    const ok = flac.FLAC__stream_encoder_process_interleaved(enc, pcm32, samples.length);
    if (!ok) throw new Error("FLAC encoding failed");

    // [4] FINISH — flushes the last frame + fires the metadata callback.
    if (!flac.FLAC__stream_encoder_finish(enc)) {
      throw new Error("FLAC encoding failed");
    }
  } finally {
    // [5] DESTROY — free the encoder even on failure paths.
    flac.FLAC__stream_encoder_delete(enc);
  }

  if (!chunks.length) throw new Error("FLAC encoding failed");

  // Merge the encoded chunks (same merge as libflacjs's data-utils).
  const total = chunks.reduce((n, c) => n + c.length, 0);
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    bytes.set(c, offset);
    offset += c.length;
  }

  // Patch STREAMINFO with the final metadata (total_samples, frame sizes,
  // MD5) — mirrors libflacjs's addFLACMetaData() for the native container,
  // with offsets converted to absolute file positions (see constants above).
  // The first chunk starts with "fLaC" + block header; STREAMINFO content
  // begins at byte 8. If the magic is missing the encoder produced
  // something unexpected — ship it unpatched rather than corrupting it.
  const headerOk = bytes.length > 42 && String.fromCharCode(...bytes.subarray(0, 4)) === "fLaC";
  if (headerOk && metadata) {
    const m = metadata as {
      min_framesize: number;
      max_framesize: number;
      total_samples: number;
      md5sum: string;
    };
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    view.setUint8(SI_MIN_FRAMESIZE, m.min_framesize >> 16);
    view.setUint8(SI_MIN_FRAMESIZE + 1, m.min_framesize >> 8);
    view.setUint8(SI_MIN_FRAMESIZE + 2, m.min_framesize);
    view.setUint8(SI_MAX_FRAMESIZE, m.max_framesize >> 16);
    view.setUint8(SI_MAX_FRAMESIZE + 1, m.max_framesize >> 8);
    view.setUint8(SI_MAX_FRAMESIZE + 2, m.max_framesize);
    view.setUint8(SI_TOTAL_SAMPLES, m.total_samples >> 24);
    view.setUint8(SI_TOTAL_SAMPLES + 1, m.total_samples >> 16);
    view.setUint8(SI_TOTAL_SAMPLES + 2, m.total_samples >> 8);
    view.setUint8(SI_TOTAL_SAMPLES + 3, m.total_samples);
    for (let i = 0; i < 16; i++) {
      view.setUint8(SI_MD5 + i, parseInt(m.md5sum.substring(i * 2, i * 2 + 2), 16));
    }
  }

  // Uint8Array → base64 (chunked — same pattern as audioToWav.ts).
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return { base64: btoa(binary), format: "flac" };
}
